// Land contours (lane C, backlog C-3; territory-visuals.md §1). The reference draws each territory
// as one polygon along the head's own path, so its edges are straight at any angle and its turns
// are round arcs. Our land is a grid, so we trace it and smooth the trace:
//
// 1 `contourLoops`: marching squares over the cell centres (the edge runs through the midpoints
//   between an owned and an unowned cell centre; saddles 5 and 10 join the owned diagonal, so a
//   territory stays connected across it). Segments are oriented with the owned side on the right
//   and linked by integer edge keys into closed loops: outer edges wind clockwise on the +y-down
//   screen, holes anticlockwise, so one path of every loop filled with the nonzero rule is the
//   land. Cells outside the sample window count as unowned, so every loop closes.
// 2 `smoothLoop`: each vertex becomes the Gaussian-weighted mean (σ = min(SMOOTH_SIGMA,
//   perimeter / 8) cells of arc length, window 3σ) of the raw vertices around it, then one closed
//   Chaikin pass. No resampling: a vertex depends only on the lattice within ~5 cells, so two
//   chunks sampling the same cells produce identical vertices (seamless chunk borders). A 30°
//   edge stays within 0.14 cell of its line (raw: 0.43), 1-cell strips keep ~1 cell of width
//   with round ends, turn staircases become arcs.
// 3 Crumbs (LV-4): loops of ≤ SMALL_LOOP_VERTICES raw vertices whose cells fill their bounding
//   box (a single cell, a domino, a 1 × 3 bar, a 2 × 2 block) skip the smoothing and become a
//   round-ended box over that box, so a single cell reads as a Ø 1 cell dot instead of shrinking
//   to a speck. An L of 3 cells or two cells touching at a corner does not fill its box and is
//   smoothed like any loop (a box would paint the empty cell as land).
// 4 Round blobs: a loop whose raw vertices all lie within ROUND_TOL_CELLS of their mean distance
//   from the loop's centroid (a lattice disc: every spawn blob) draws as a true circle of the same
//   area, as the reference's start land is a 50-gon. Smoothing alone leaves a lattice disc 0.2
//   cell out of round (bulging diagonals, flat runs between). A hole of that shape (a spawn
//   carved into older land) gets the same circle, so the two edges still meet.
// Pure; no canvas.

/** A flat list of polygons, each `[x0, y0, x1, y1, …]` in cells (world units). */
export type Polygons = number[][];

/**
 * The smoothing width (cells of arc length; LV-1). 1.5 keeps a 30° edge within 0.14 cell of its
 * line (1.25: 0.16) while a 1-cell strip keeps its width and junction corners stay ~1 cell round.
 */
export const SMOOTH_SIGMA = 1.5;
/** Loops with at most this many raw vertices draw as round crumbs (LV-4). */
export const SMALL_LOOP_VERTICES = 8;
/**
 * A loop is a round blob (drawn as a circle) when every raw vertex is within this many cells of
 * the mean radius around the centroid. A lattice disc's staircase deviates ≤ 0.45 cell (radius 5:
 * 4.66–5.50 around 5.06); one extra cell on its edge already deviates ≈ 1 cell.
 */
export const ROUND_TOL_CELLS = 0.5;
/** Fewest raw vertices of a round blob (below: crumbs and smoothing do fine). */
export const ROUND_MIN_VERTICES = 12;
/** Circle vertices of a round blob (the reference's start land is a 50-gon). */
export const ROUND_SEGMENTS = 48;
/**
 * Cells a sample window must reach past the area that is drawn so that smoothing near the
 * window's edge (where outside cells read as unowned) never shows: > 3σ + 1 (the Gaussian reach
 * plus the Chaikin blend and the half-cell midpoint offset).
 */
export const SMOOTH_PAD = 6;

// Marching-squares segments per case (bits tl 8, tr 4, br 2, bl 1), owned side on the right.
// Edges: 0 top H(x, y), 1 right V(x+1, y), 2 bottom H(x, y+1), 3 left V(x, y).
const T = 0;
const R = 1;
const B = 2;
const L = 3;
const SEGMENTS: readonly (readonly number[])[] = [
  [], // 0
  [L, B], // 1 bl
  [B, R], // 2 br
  [L, R], // 3 bl br
  [R, T], // 4 tr
  [L, T, R, B], // 5 tr bl (saddle, joined)
  [B, T], // 6 tr br
  [L, T], // 7 tr br bl
  [T, L], // 8 tl
  [T, B], // 9 tl bl
  [T, R, B, L], // 10 tl br (saddle, joined)
  [T, R], // 11 tl br bl
  [R, L], // 12 tl tr
  [R, B], // 13 tl tr bl
  [B, L], // 14 tl tr br
  [], // 15
];

/**
 * Closed contour loops of owner `id` over the window of cells `x0 … x1` × `y0 … y1` (inclusive;
 * cells outside it count as unowned). `at(cx, cy)` reads a cell's owner. Each loop is a flat
 * `[x, y, …]` of edge midpoints in cells, owned side on the right.
 */
export function contourLoops(
  at: (cx: number, cy: number) => number,
  id: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): Polygons {
  const w = x1 - x0 + 1;
  const hgt = y1 - y0 + 1;
  if (w <= 0 || hgt <= 0) return [];
  // Ownership with a ring of unowned samples around the window (local coords +1).
  const sw = w + 2;
  const sh = hgt + 2;
  const own = new Uint8Array(sw * sh);
  let any = false;
  for (let y = 0; y < hgt; y++) {
    for (let x = 0; x < w; x++) {
      if (at(x0 + x, y0 + y) === id) {
        own[(y + 1) * sw + x + 1] = 1;
        any = true;
      }
    }
  }
  if (!any) return [];
  return traceOwned(own, sw, sh, x0 - 1, y0 - 1);
}

/**
 * Loops of a 0/1 sample grid `own` (sw × sh, row-major; its outer ring must be 0). Sample (i, j)
 * is the cell (ox + i, oy + j). Shared by the land layer and the effects (flash, exit fade).
 */
export function traceOwned(
  own: Uint8Array,
  sw: number,
  sh: number,
  ox: number,
  oy: number,
): Polygons {
  // Edge keys: ((j * sw + i) << 1) | vertical. H(i, j) joins samples (i, j)–(i+1, j), midpoint
  // (i + 1, j + 0.5) in local cells; V(i, j) joins (i, j)–(i, j+1), midpoint (i + 0.5, j + 1).
  const next = new Int32Array(sw * sh * 2).fill(-1);
  const starts: number[] = [];
  for (let j = 0; j + 1 < sh; j++) {
    const row = j * sw;
    for (let i = 0; i + 1 < sw; i++) {
      const c =
        ((own[row + i] as number) << 3) |
        ((own[row + i + 1] as number) << 2) |
        ((own[row + sw + i + 1] as number) << 1) |
        (own[row + sw + i] as number);
      if (c === 0 || c === 15) continue;
      const segs = SEGMENTS[c] as readonly number[];
      for (let s = 0; s < segs.length; s += 2) {
        const a = edgeKey(segs[s] as number, i, j, sw);
        const b = edgeKey(segs[s + 1] as number, i, j, sw);
        next[a] = b;
        starts.push(a);
      }
    }
  }
  const loops: Polygons = [];
  const seen = new Uint8Array(next.length);
  for (const k0 of starts) {
    if (seen[k0]) continue;
    const loop: number[] = [];
    let k = k0;
    while (k >= 0 && !seen[k]) {
      seen[k] = 1;
      const e = k >> 1;
      const i = e % sw;
      const j = (e - i) / sw;
      if (k & 1) loop.push(ox + i + 0.5, oy + j + 1);
      else loop.push(ox + i + 1, oy + j + 0.5);
      k = next[k] as number;
    }
    if (loop.length >= 6) loops.push(loop);
  }
  return loops;
}

function edgeKey(edge: number, i: number, j: number, sw: number): number {
  switch (edge) {
    case T:
      return (j * sw + i) << 1;
    case R:
      return ((j * sw + i + 1) << 1) | 1;
    case B:
      return ((j + 1) * sw + i) << 1;
    default:
      return ((j * sw + i) << 1) | 1;
  }
}

/** Twice the signed area of a polygon (positive = clockwise on a +y-down screen). */
export function polygonArea2(p: readonly number[]): number {
  let a = 0;
  const n = p.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a +=
      (p[i * 2] as number) * (p[j * 2 + 1] as number) -
      (p[j * 2] as number) * (p[i * 2 + 1] as number);
  }
  return a;
}

/** The net area (cells²) of loops (holes, wound the other way, subtract). */
export function polygonsArea(polys: Polygons): number {
  let a = 0;
  for (const p of polys) a += polygonArea2(p) / 2;
  return Math.abs(a);
}

/**
 * A raw loop smoothed for drawing: Gaussian along the arc (σ = min(`sigma`, perimeter / 8),
 * window 3σ) then one closed Chaikin pass; a crumb (≤ SMALL_LOOP_VERTICES vertices filling its
 * box) becomes a round-ended box over its bounding box instead, and a round blob a circle of the
 * same area. The winding is kept.
 */
export function smoothLoop(p: readonly number[], sigma: number = SMOOTH_SIGMA): number[] {
  const n = p.length / 2;
  if (n <= SMALL_LOOP_VERTICES && fillsBox(p)) return crumb(p);
  if (n >= ROUND_MIN_VERTICES) {
    const c = roundBlob(p);
    if (c !== null) return circleLoop(c.x, c.y, c.r, polygonArea2(p) < 0);
  }
  const seg = new Float64Array(n);
  let per = 0;
  for (let i = 0; i < n; i++) {
    const j = i + 1 === n ? 0 : i + 1;
    const d = Math.hypot(
      (p[j * 2] as number) - (p[i * 2] as number),
      (p[j * 2 + 1] as number) - (p[i * 2 + 1] as number),
    );
    seg[i] = d;
    per += d;
  }
  const s = Math.min(sigma, per / 8);
  const lim = 3 * s;
  const inv = 1 / (2 * s * s);
  const g = new Float64Array(n * 2);
  for (let i = 0; i < n; i++) {
    let wx = p[i * 2] as number;
    let wy = p[i * 2 + 1] as number;
    let w = 1;
    let d = 0;
    let j = i;
    for (let k = 1; k < n; k++) {
      d += seg[j] as number;
      j = j + 1 === n ? 0 : j + 1;
      if (d > lim) break;
      const e = Math.exp(-d * d * inv);
      wx += e * (p[j * 2] as number);
      wy += e * (p[j * 2 + 1] as number);
      w += e;
    }
    d = 0;
    j = i;
    for (let k = 1; k < n; k++) {
      j = j === 0 ? n - 1 : j - 1;
      d += seg[j] as number;
      if (d > lim) break;
      const e = Math.exp(-d * d * inv);
      wx += e * (p[j * 2] as number);
      wy += e * (p[j * 2 + 1] as number);
      w += e;
    }
    g[i * 2] = wx / w;
    g[i * 2 + 1] = wy / w;
  }
  // One closed Chaikin pass (¼–¾).
  const out = new Array<number>(n * 4);
  for (let i = 0; i < n; i++) {
    const j = i + 1 === n ? 0 : i + 1;
    const ax = g[i * 2] as number;
    const ay = g[i * 2 + 1] as number;
    const bx = g[j * 2] as number;
    const by = g[j * 2 + 1] as number;
    out[i * 4] = ax * 0.75 + bx * 0.25;
    out[i * 4 + 1] = ay * 0.75 + by * 0.25;
    out[i * 4 + 2] = ax * 0.25 + bx * 0.75;
    out[i * 4 + 3] = ay * 0.25 + by * 0.75;
  }
  return out;
}

/** The raw loop's bounds (its extreme vertices lie on its cells' outer boundary). */
function loopBounds(p: readonly number[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < p.length; i += 2) {
    const x = p[i] as number;
    const y = p[i + 1] as number;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * True when the loop's cells fill their bounding box: the raw loop of a full w × h block runs
 * through the edge midpoints, so it is the box minus four ⅛-cell corner triangles (w·h − ½). An
 * L or a diagonal pair over the same box encloses less.
 */
export function fillsBox(p: readonly number[]): boolean {
  const b = loopBounds(p);
  const box = (b.maxX - b.minX) * (b.maxY - b.minY);
  return Math.abs(Math.abs(polygonArea2(p)) / 2 - (box - 0.5)) < 1e-6;
}

/**
 * The circle a round raw loop stands for (centroid, equal-area radius), or null when some raw
 * vertex is more than ROUND_TOL_CELLS off the loop's mean radius.
 */
export function roundBlob(p: readonly number[]): { x: number; y: number; r: number } | null {
  const n = p.length / 2;
  const a2 = polygonArea2(p);
  if (Math.abs(a2) < 1e-9) return null;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < n; i++) {
    const j = i + 1 === n ? 0 : i + 1;
    const xi = p[i * 2] as number;
    const yi = p[i * 2 + 1] as number;
    const xj = p[j * 2] as number;
    const yj = p[j * 2 + 1] as number;
    const cr = xi * yj - xj * yi;
    cx += (xi + xj) * cr;
    cy += (yi + yj) * cr;
  }
  cx /= 3 * a2;
  cy /= 3 * a2;
  let sum = 0;
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < n; i++) {
    const d = Math.hypot((p[i * 2] as number) - cx, (p[i * 2 + 1] as number) - cy);
    sum += d;
    if (d < lo) lo = d;
    if (d > hi) hi = d;
  }
  const mean = sum / n;
  if (hi - mean > ROUND_TOL_CELLS || mean - lo > ROUND_TOL_CELLS) return null;
  return { x: cx, y: cy, r: Math.sqrt(Math.abs(a2) / 2 / Math.PI) };
}

/** A ROUND_SEGMENTS-gon, clockwise on screen (+y down), or anticlockwise for a hole. */
function circleLoop(cx: number, cy: number, r: number, hole: boolean): number[] {
  const out: number[] = [];
  for (let k = 0; k < ROUND_SEGMENTS; k++) {
    const a = ((hole ? -k : k) / ROUND_SEGMENTS) * Math.PI * 2;
    out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return out;
}

/** A crumb loop as a round-ended box (stadium, or a circle when square) over its bounds. */
function crumb(p: readonly number[]): number[] {
  const { minX, minY, maxX, maxY } = loopBounds(p);
  // The raw loop's extreme vertices lie on the cells' outer boundary, so its bounds are the
  // cells' bounds (a single cell: exactly the 1 × 1 cell).
  const r = Math.min(maxX - minX, maxY - minY) / 2;
  const cx0 = minX + r;
  const cx1 = maxX - r;
  const cy0 = minY + r;
  const cy1 = maxY - r;
  const STEPS = 6; // per quarter arc
  const out: number[] = [];
  // Clockwise on screen (+y down): right side downward first.
  const corners: [number, number, number][] = [
    [cx1, cy0, -Math.PI / 2],
    [cx1, cy1, 0],
    [cx0, cy1, Math.PI / 2],
    [cx0, cy0, Math.PI],
  ];
  for (const [cx, cy, a0] of corners) {
    for (let k = 0; k <= STEPS; k++) {
      const a = a0 + (k / STEPS) * (Math.PI / 2);
      out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    }
  }
  // Keep the loop's winding (a hole stays a hole).
  if (polygonArea2(p) < 0) {
    const rev: number[] = [];
    for (let i = out.length - 2; i >= 0; i -= 2) rev.push(out[i] as number, out[i + 1] as number);
    return rev;
  }
  return out;
}

/** Every loop of owner `id` in the window, smoothed for drawing. */
export function smoothContours(
  at: (cx: number, cy: number) => number,
  id: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  sigma: number = SMOOTH_SIGMA,
): Polygons {
  return contourLoops(at, id, x0, y0, x1, y1).map((l) => smoothLoop(l, sigma));
}

/**
 * Smoothed loops around a set of cells (grid indices on a `cols`-wide grid): the capture flash and
 * the exit fade follow the same rounded outline as the land they cover.
 */
export function cellSetContours(cells: readonly number[], cols: number): Polygons {
  if (cells.length === 0) return [];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of cells) {
    const x = c % cols;
    const y = (c - x) / cols;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const sw = maxX - minX + 3;
  const sh = maxY - minY + 3;
  const own = new Uint8Array(sw * sh);
  for (const c of cells) {
    const x = c % cols;
    const y = (c - x) / cols;
    own[(y - minY + 1) * sw + (x - minX + 1)] = 1;
  }
  return traceOwned(own, sw, sh, minX - 1, minY - 1).map((l) => smoothLoop(l));
}

/** Adds `polys` (cells) to the current path of `ctx` through `x = ox + px·s, y = oy + py·s`. */
export function pathPolygons(
  ctx: {
    moveTo(x: number, y: number): void;
    lineTo(x: number, y: number): void;
    closePath(): void;
  },
  polys: Polygons,
  ox = 0,
  oy = 0,
  s = 1,
): void {
  for (const p of polys) {
    if (p.length < 6) continue;
    ctx.moveTo(ox + (p[0] as number) * s, oy + (p[1] as number) * s);
    for (let k = 2; k < p.length; k += 2)
      ctx.lineTo(ox + (p[k] as number) * s, oy + (p[k + 1] as number) * s);
    ctx.closePath();
  }
}
